import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { IconDirective, IconSetService } from '@coreui/icons-angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Observable, Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { I18nService } from 'i18n';
import { FoundingMemberBadgeComponent } from 'shared';
import { CommunityService } from '../../../services/community.service';
import { CommunityAdminService } from '../../../services/community-admin.service';
import { CommunityUnreadService } from '../../../services/community-unread.service';
import { CommunityLanguageCheckService } from '../../../services/community-language-check.service';
import {
  CommunityChannel,
  CommunityChannelLink,
  CommunityChannelMode,
  CommunityGroup,
  CommunityLegacyTopic,
  CommunityOverview,
  CommunityRoleBrief,
  CommunitySanction,
  CommunityThread,
  CommunityUserBrief,
  communityErrorKey,
  reactionTotal,
  communityBodyBlank,
  communityHtmlToText,
} from '../../../models/community.models';
import { CommunityEditorComponent } from '../../../components/community-editor/community-editor.component';
import { CommunityBadgesComponent } from '../../../components/community-badges/community-badges.component';
import { CommunitySanctionNoticeComponent } from '../../../components/community-sanction-notice/community-sanction-notice.component';
import { CommunityReasonModalComponent } from '../../../components/community-reason-modal/community-reason-modal.component';
import { CommunityLanguageConfirmModalComponent } from '../../../components/community-language-confirm-modal/community-language-confirm-modal.component';

/** Per-channel draft state of the admin "manage channels" modal. */
interface ChannelEdit {
  name: string;
  description: string;
  mode: CommunityChannelMode;
  read_only: boolean;
}

/** Draft of the inline pinned-link form (limits mirror CommunityChannelLink validations). */
interface LinkDraft {
  title: string;
  description: string;
  url: string;
}

const EMPTY_LINK: LinkDraft = { title: '', description: '', url: '' };

/**
 * The network shell: groups → channels on the left, the thread list of the
 * current channel (or the newest threads of every readable channel) on the
 * right. Serves both `community` and `community/c/:channelId`.
 */
@Component({
  selector: 'app-community-list-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    RouterLinkActive,
    IconDirective,
    TranslocoModule,
    CommunityEditorComponent,
    FoundingMemberBadgeComponent,
    CommunityBadgesComponent,
    CommunitySanctionNoticeComponent,
    CommunityReasonModalComponent,
    CommunityLanguageConfirmModalComponent
  ],
  templateUrl: './community-list-page.component.html',
  styleUrl: './community-list-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityListPageComponent implements OnInit, OnDestroy {
  readonly modes: CommunityChannelMode[] = ['discussion', 'news', 'qa'];

  // --- Structure -------------------------------------------------------------
  overview = signal<CommunityOverview | null>(null);
  overviewLoading = signal(false);
  /** Translation key of a blocking error (403 codes) shown instead of the shell. */
  overviewError = signal<string | null>(null);

  /** Channel from the route; null on the "all threads" view. */
  channelId = signal<number | null>(null);
  channel = signal<CommunityChannel | null>(null);
  channelError = signal<string | null>(null);
  /** Legacy topics of an archive channel (read-only filter). */
  topics = signal<CommunityLegacyTopic[]>([]);
  activeTopicId = signal<number | null>(null);

  isAdmin = computed(() => this.overview()?.is_admin ?? false);
  muted = computed(() => this.overview()?.muted ?? false);
  /** Moderates at least one channel: the moderation page is offered. */
  canModerate = computed(() => this.overview()?.can_moderate === true);
  sanctions = computed<CommunitySanction[]>(() => this.overview()?.sanctions ?? []);
  warnings = computed<CommunitySanction[]>(() => this.overview()?.warnings ?? []);
  /** Unread private messages, polled while the shell is open. */
  unreadMessages = this.unread.count;
  groups = computed<CommunityGroup[]>(() => this.overview()?.groups ?? []);
  optInRoles = computed<CommunityRoleBrief[]>(() => this.overview()?.opt_in_roles ?? []);
  purchaseUrl = computed(() => this.overview()?.purchase_url ?? null);
  /**
   * Whether to offer the admin page: admins always, area admins once the
   * structure endpoint has answered 200 for them (they are not `user_type` admins).
   */
  canManage = signal(false);

  /** Every channel the viewer may enter (locked teasers excluded). */
  openChannels = computed<CommunityChannel[]>(() =>
    this.groups().filter(g => !g.locked).flatMap(g => g.channels)
  );
  /** Channels the viewer may start a thread in — feeds the compose channel select. */
  postableChannels = computed<CommunityChannel[]>(() =>
    this.openChannels().filter(c => c.permissions.create_thread)
  );

  /**
   * The number next to a channel in the nav: what is NEW for the viewer, not
   * how much the channel holds. Archived boards (`unread_count === null`) never
   * receive anything again, so they keep showing their total instead — that
   * count is what the channel list offers them for. A backend that does not
   * send `unread_count` yet falls back to the old total everywhere.
   */
  channelBadge(channel: CommunityChannel): { text: string; unread: boolean } | null {
    const unread = channel.unread_count;
    if (unread === undefined) {
      return channel.thread_count ? { text: String(channel.thread_count), unread: false } : null;
    }
    if (unread) return { text: unread > 99 ? '99+' : String(unread), unread: true };
    if (unread === null && channel.thread_count) return { text: String(channel.thread_count), unread: false };
    return null;
  }

  /**
   * Private Room channel ids from the overview: the thread brief may not carry
   * `private` itself, so the "all posts" list falls back to this.
   */
  privateChannelIds = computed<Set<number>>(() =>
    new Set(this.openChannels().filter(c => c.private === true).map(c => c.id))
  );

  /** Whether the opened channel is a Private Room (copy protection + notice). */
  channelIsPrivate = computed<boolean>(() => this.channel()?.private === true);

  threadIsPrivate(thread: CommunityThread): boolean {
    return thread.channel?.private === true || this.privateChannelIds().has(thread.channel_id);
  }

  /**
   * Copy protection in Private Rooms (as far as practicable): no context menu on
   * the posts -- except inside the viewer's own input fields, where spelling
   * suggestions and paste must keep working.
   */
  blockContextMenu(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, [contenteditable="true"], app-community-editor')) return;
    event.preventDefault();
  }

  // --- Threads ---------------------------------------------------------------
  threads = signal<CommunityThread[]>([]);
  loading = signal(false);
  page = signal(1);
  totalPages = signal(1);
  perPage = 20;
  /** From the thread list response; only meaningful inside a channel. */
  canCreateThread = signal(false);
  /** Pinned threads sort first; on page 1 they get their own divider. */
  pinnedCount = computed(() => (this.page() === 1 ? this.threads().filter(t => t.pinned).length : 0));

  // --- Q&A (§17): solved/open filter, only meaningful in a `qa` channel ------
  qaFilter = signal<'all' | 'open' | 'solved'>('all');

  // --- Channel subscription (Phase 4 notifications) --------------------------
  subscribeBusy = signal(false);

  // --- Search (§13) ------------------------------------------------------------
  searchQuery = '';

  readonly reactionTotal = reactionTotal;

  // --- Pinned links ------------------------------------------------------------
  activeLinks = computed<CommunityChannelLink[]>(() => (this.channel()?.links ?? []).filter(l => l.active));
  inactiveLinks = computed<CommunityChannelLink[]>(() => (this.channel()?.links ?? []).filter(l => !l.active));
  canManageLinks = computed<boolean>(() => this.channel()?.permissions.manage_links === true);
  /** 'new', a link id, or null while the form is closed. */
  linkFormTarget = signal<'new' | number | null>(null);
  linkDraft: LinkDraft = { ...EMPTY_LINK };
  linkBusy = signal(false);
  linkError = signal<string | null>(null);

  /** Inline (non-blocking) error, e.g. a 403 on compose or opt-in. */
  actionError = signal<string | null>(null);

  canCompose = computed<boolean>(() => {
    if (this.muted()) return false;
    return this.channelId() != null ? this.canCreateThread() : this.postableChannels().length > 0;
  });

  createOpen = signal(false);
  titleDraft = '';
  bodyDraft = '';
  createChannelId: number | null = null;
  submitting = signal(false);

  /** The channel picked in the compose form — drives the editor's mention/upload/embed extras. */
  get createChannelObj(): CommunityChannel | null {
    return this.openChannels().find(c => c.id === this.createChannelId) ?? null;
  }
  get createCanMention(): boolean {
    return this.createChannelObj?.permissions.mention === true;
  }
  get createCanUploadImage(): boolean {
    return this.createChannelObj?.permissions.upload_image === true && (this.createChannelObj?.allowed_uploads ?? []).includes('image');
  }
  get createCanUploadFile(): boolean {
    return this.createChannelObj?.permissions.upload_file === true && (this.createChannelObj?.allowed_uploads ?? []).includes('file');
  }
  get createCanEmbedVideo(): boolean {
    return this.createChannelObj?.permissions.embed_video === true && (this.createChannelObj?.allowed_uploads ?? []).includes('video_link');
  }

  // --- Opt-in -----------------------------------------------------------------
  optInPending = signal<string | null>(null);

  // --- Manage channels modal (admin) ------------------------------------------
  manageOpen = signal(false);
  manageGroupId = signal<number | null>(null);
  manageGroup = computed<CommunityGroup | null>(() =>
    this.groups().find(g => g.id === this.manageGroupId()) ?? null
  );
  manageChannels = computed<CommunityChannel[]>(() => this.manageGroup()?.channels ?? []);
  manageError = signal<string | null>(null);
  manageBusy = signal(false);
  editingChannelId = signal<number | null>(null);
  channelEdit: ChannelEdit = { name: '', description: '', mode: 'discussion', read_only: false };
  newChannel: ChannelEdit = { name: '', description: '', mode: 'discussion', read_only: false };
  /** Channel whose deletion was refused (422: has threads) and now needs a move target. */
  deletePendingChannel = signal<CommunityChannel | null>(null);
  deleteMoveTargetId: number | null = null;
  deleteMoveTargets = computed<CommunityChannel[]>(() => {
    const pending = this.deletePendingChannel();
    if (!pending) return [];
    return this.manageChannels().filter(c => c.id !== pending.id);
  });

  // Muted users (admin), managed from the same modal
  hiddenUsers = signal<CommunityUserBrief[]>([]);
  /** User being un-muted; the reason is mandatory since Phase 2. */
  unhideTarget = signal<CommunityUserBrief | null>(null);
  unhideBusy = signal(false);
  unhideError = signal<string | null>(null);

  private langSub?: Subscription;
  private routeSub?: Subscription;

  constructor(
    private communityService: CommunityService,
    private communityAdminService: CommunityAdminService,
    private unread: CommunityUnreadService,
    private languageCheck: CommunityLanguageCheckService,
    private i18n: I18nService,
    private transloco: TranslocoService,
    private route: ActivatedRoute,
    private router: Router,
    private iconSet: IconSetService
  ) {}

  ngOnInit(): void {
    this.loadOverview();
    this.unread.start();
    this.routeSub = this.route.paramMap.subscribe(params => {
      // `community/g/:groupKey`: resolve to the group's first visible channel once
      // the overview is here (it may already be, or arrive later — both handled).
      const groupKey = params.get('groupKey');
      if (groupKey) {
        this.pendingGroupKey = groupKey;
        this.openPendingGroup();
        return;
      }
      const raw = params.get('channelId');
      const id = raw ? Number(raw) : null;
      this.channelId.set(id && !Number.isNaN(id) ? id : null);
      this.activeTopicId.set(null);
      this.qaFilter.set('all');
      this.createOpen.set(false);
      this.actionError.set(null);
      this.loadChannel();
      this.load(1);
    });
    // Names come back in the UI language, so the structure reloads on a switch.
    // skip(1): the BehaviorSubject replays the current language synchronously.
    this.langSub = this.i18n.currentLanguage$.pipe(skip(1)).subscribe(() => {
      this.loadOverview();
      this.loadChannel();
      this.load(this.page());
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.unread.stop();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  // --- Links -----------------------------------------------------------------

  allLink(): string {
    return `/${this.lang}/community`;
  }

  /** Group key from `community/g/:groupKey`, waiting for the overview to resolve it. */
  private pendingGroupKey: string | null = null;

  /**
   * Replaces a `community/g/<key>` URL with the group's first visible channel.
   * A group the viewer does not hold (no role, or locked) falls back to the
   * community root — it must not look like a locked door.
   */
  private openPendingGroup(): void {
    const key = this.pendingGroupKey;
    if (!key || !this.overview()) return;
    this.pendingGroupKey = null;
    const group = this.groups().find(g => g.key === key && !g.locked);
    const channel = group?.channels[0];
    this.router.navigateByUrl(channel ? this.channelLink(channel.id) : `/${this.lang}/community`, { replaceUrl: true });
  }

  channelLink(channelId: number): string {
    return `/${this.lang}/community/c/${channelId}`;
  }

  threadLink(id: number): string {
    return `/${this.lang}/community/${id}`;
  }

  memberLink(userId: number): string {
    return `/${this.lang}/community/u/${userId}`;
  }

  messagesLink(): string {
    return `/${this.lang}/community/messages`;
  }

  moderationLink(): string {
    return `/${this.lang}/community/moderation`;
  }

  /**
   * The notification settings — the only place a member can switch a kind off,
   * announcements included. Reachable through the header bell as well, but that
   * is three clicks deep and nobody finds it there, so the community shell
   * carries its own entry next to the other personal ones.
   */
  notificationSettingsLink(): string {
    return `/${this.lang}/community/settings`;
  }

  /** Pending/rejected badge on a listed thread: for its author and for moderators. */
  approvalBadge(th: CommunityThread): 'pending' | 'rejected' | null {
    const status = th.approval_status;
    if (status !== 'pending' && status !== 'rejected') return null;
    return th.is_mine || th.can_moderate || th.can_admin ? status : null;
  }

  /** Mobile `<select>`: navigate to the chosen channel ('' = all threads). */
  onMobileSelect(value: string): void {
    this.router.navigateByUrl(value ? this.channelLink(Number(value)) : this.allLink());
  }

  /**
   * Group icons arrive as CoreUI names (`cil-heart`). Only render the ones the
   * app actually registered in its IconSetService, else cIcon warns and paints
   * nothing anyway.
   */
  iconName(name: string | null | undefined): string | null {
    if (!name) return null;
    const camel = name.includes('-')
      ? name.replace(/([-_][a-z0-9])/gi, m => m.toUpperCase().replace('-', '').replace('_', ''))
      : name;
    return this.iconSet.icons?.[camel] ? camel : null;
  }

  // --- Loading ---------------------------------------------------------------

  loadOverview(): void {
    this.overviewLoading.set(true);
    this.communityService.getOverview(this.lang).subscribe({
      next: res => {
        this.overview.set(res);
        this.overviewError.set(null);
        this.overviewLoading.set(false);
        this.openPendingGroup();
        if (this.manageGroupId() == null && res.groups.length) {
          this.manageGroupId.set(res.groups[0].id);
        }
        this.resolveCanManage(res.is_admin);
      },
      error: err => {
        this.overviewLoading.set(false);
        this.overviewError.set(communityErrorKey(err));
      }
    });
  }

  /**
   * Admins need no probe. Since Phase 2 the overview says `can_manage` itself;
   * only on an older backend (flag absent) is the admin API asked once per
   * session (cached).
   */
  private resolveCanManage(isAdmin: boolean): void {
    const flag = this.overview()?.can_manage;
    if (isAdmin || flag === true) {
      this.canManage.set(true);
      return;
    }
    if (flag === false) {
      this.canManage.set(false);
      return;
    }
    this.communityAdminService.canManage().subscribe(ok => this.canManage.set(ok));
  }

  adminLink(): string {
    return `/${this.lang}/community/admin`;
  }

  loadChannel(): void {
    const id = this.channelId();
    this.channelError.set(null);
    if (id == null) {
      this.channel.set(null);
      this.topics.set([]);
      return;
    }
    this.communityService.getChannel(id, this.lang).subscribe({
      next: res => {
        this.channel.set(res.channel);
        this.topics.set(res.topics);
      },
      error: err => {
        this.channel.set(null);
        this.topics.set([]);
        this.channelError.set(err?.status === 404 ? 'community.channel.not_found' : communityErrorKey(err));
      }
    });
  }

  // --- Pinned links ------------------------------------------------------------

  openLinkForm(link?: CommunityChannelLink): void {
    this.linkError.set(null);
    this.linkDraft = link
      ? { title: link.title, description: link.description ?? '', url: link.url }
      : { ...EMPTY_LINK };
    this.linkFormTarget.set(link ? link.id : 'new');
  }

  cancelLinkForm(): void {
    this.linkFormTarget.set(null);
  }

  linkDraftValid(): boolean {
    const url = (this.linkDraft.url || '').trim();
    return !!(this.linkDraft.title || '').trim() && /^https?:\/\/\S+$/i.test(url);
  }

  submitLinkForm(): void {
    const channelId = this.channelId();
    const target = this.linkFormTarget();
    if (channelId == null || target == null || !this.linkDraftValid()) return;
    const payload = {
      title: this.linkDraft.title.trim(),
      description: (this.linkDraft.description || '').trim() || null,
      url: this.linkDraft.url.trim()
    };
    this.runLink(
      target === 'new'
        ? this.communityService.createLink(channelId, payload)
        : this.communityService.updateLink(target, payload),
      () => this.linkFormTarget.set(null)
    );
  }

  setLinkActive(link: CommunityChannelLink, active: boolean): void {
    this.runLink(this.communityService.updateLink(link.id, { active }));
  }

  deleteLink(link: CommunityChannelLink, confirmMsg: string): void {
    if (!confirm(confirmMsg)) return;
    this.runLink(this.communityService.deleteLink(link.id));
  }

  /** Links ride on the channel payload, so a change simply reloads the channel. */
  private runLink(request: Observable<unknown>, onSuccess?: () => void): void {
    this.linkError.set(null);
    this.linkBusy.set(true);
    request.subscribe({
      next: () => {
        onSuccess?.();
        this.linkBusy.set(false);
        this.loadChannel();
      },
      error: err => {
        this.linkBusy.set(false);
        this.linkError.set(communityErrorKey(err));
      }
    });
  }

  selectTopic(topicId: number | null): void {
    this.activeTopicId.set(topicId);
    this.load(1);
  }

  // --- Q&A (§17) ---------------------------------------------------------------

  selectQaFilter(filter: 'all' | 'open' | 'solved'): void {
    this.qaFilter.set(filter);
    this.load(1);
  }

  // --- Channel subscription ------------------------------------------------------

  toggleSubscribe(): void {
    const ch = this.channel();
    if (!ch) return;
    this.subscribeBusy.set(true);
    const request = ch.subscribed
      ? this.communityService.unsubscribeChannel(ch.id)
      : this.communityService.subscribeChannel(ch.id);
    request.subscribe({
      next: res => {
        this.subscribeBusy.set(false);
        this.channel.update(c => (c ? { ...c, subscribed: res.subscribed } : c));
      },
      error: err => {
        this.subscribeBusy.set(false);
        this.actionError.set(communityErrorKey(err));
      }
    });
  }

  // --- Search --------------------------------------------------------------------

  submitSearch(): void {
    const q = (this.searchQuery || '').trim();
    if (!q) return;
    this.router.navigateByUrl(`/${this.lang}/community/search?q=${encodeURIComponent(q)}`);
  }

  load(page = 1): void {
    this.loading.set(true);
    const ch = this.channel();
    this.communityService
      .listThreads({
        channelId: this.channelId(),
        topicId: this.activeTopicId(),
        page,
        perPage: this.perPage,
        uiLocale: this.lang,
        solved: ch?.mode === 'qa' && this.qaFilter() !== 'all' ? this.qaFilter() === 'solved' : null
      })
      .subscribe({
        next: res => {
          this.threads.set(res.data);
          this.page.set(res.page);
          this.totalPages.set(res.total_pages);
          this.canCreateThread.set(res.can_create_thread === true);
          this.loading.set(false);
        },
        error: err => {
          this.threads.set([]);
          this.loading.set(false);
          if (err?.status !== 404) this.actionError.set(communityErrorKey(err));
        }
      });
  }

  // --- Compose ---------------------------------------------------------------

  openCreate(): void {
    this.titleDraft = '';
    this.bodyDraft = '';
    this.actionError.set(null);
    const current = this.channelId();
    this.createChannelId = current != null && this.canCreateThread()
      ? current
      : this.postableChannels()[0]?.id ?? null;
    this.createOpen.set(true);
  }

  cancelCreate(): void {
    this.createOpen.set(false);
  }

  /** A body of nothing but empty lines is not a post — the backend refuses it too. */
  bodyBlank(html: string): boolean {
    return communityBodyBlank(html);
  }

  /**
   * The spinner stays on across the language check (§7.3), so the button cannot
   * be pressed twice while the server is still looking at the draft. A cancelled
   * dialog is not an error: the composer simply stays open with the text in it.
   */
  async submitCreate(): Promise<void> {
    const title = (this.titleDraft || '').trim();
    const body = (this.bodyDraft || '').trim();
    const channelId = this.createChannelId;
    if (!title || communityBodyBlank(body) || channelId == null) return;

    this.submitting.set(true);
    // A new thread has no thread to inherit from: what it would be stored as is
    // the writer's current UI language.
    const decision = await this.languageCheck.confirmLocale({
      title,
      body,
      channelId,
      expected: this.i18n.getLanguage()
    });
    if (!decision.proceed) {
      this.submitting.set(false);
      return;
    }

    this.communityService.createThread(channelId, title, body, decision.locale).subscribe({
      next: res => {
        this.submitting.set(false);
        this.createOpen.set(false);
        this.loadOverview(); // thread counts
        this.router.navigateByUrl(this.threadLink(res.thread.id));
      },
      error: err => {
        this.submitting.set(false);
        this.actionError.set(communityErrorKey(err));
      }
    });
  }

  // --- Opt-in roles ----------------------------------------------------------

  /**
   * The line under the heading of an opt-in card. A role's `description` column
   * is the ADMINISTRATOR's note — it is written in the rights matrix, in English,
   * and cites the spec ("Opens the open sales entry level (F8/F16)"), which is
   * not what the member reading the card needs. So a translated string per role
   * key wins when there is one; roles an admin created themselves have no such
   * key and keep their own description.
   */
  optInDescription(role: CommunityRoleBrief): string {
    const key = `community.opt_in.description.${role.key}`;
    const text = this.transloco.translate(key);
    return !text || text === key ? (role.description ?? '') : text;
  }

  /**
   * Join a self-service role, then jump to the first channel it opened. The
   * "new" channels are those in the reloaded overview that were not enterable
   * before — the backend does not tell us which group a role unlocks.
   */
  optIn(role: CommunityRoleBrief): void {
    const before = new Set(this.openChannels().map(c => c.id));
    this.optInPending.set(role.key);
    this.actionError.set(null);
    this.communityService.optIn(role.key).subscribe({
      next: () => {
        this.communityService.getOverview(this.lang).subscribe({
          next: res => {
            this.overview.set(res);
            this.optInPending.set(null);
            const opened = res.groups
              .filter(g => !g.locked)
              .flatMap(g => g.channels)
              .find(c => !before.has(c.id));
            if (opened) this.router.navigateByUrl(this.channelLink(opened.id));
          },
          error: err => {
            this.optInPending.set(null);
            this.actionError.set(communityErrorKey(err));
          }
        });
      },
      error: err => {
        this.optInPending.set(null);
        this.actionError.set(communityErrorKey(err));
      }
    });
  }

  // --- Manage channels (admin) -----------------------------------------------

  openManage(): void {
    this.manageError.set(null);
    this.editingChannelId.set(null);
    this.deletePendingChannel.set(null);
    this.newChannel = { name: '', description: '', mode: 'discussion', read_only: false };
    if (this.manageGroupId() == null) {
      const current = this.channel()?.group_id ?? this.groups()[0]?.id ?? null;
      this.manageGroupId.set(current);
    }
    this.loadHiddenUsers();
    this.manageOpen.set(true);
  }

  closeManage(): void {
    this.manageOpen.set(false);
  }

  selectManageGroup(value: string | number | null): void {
    this.manageGroupId.set(value == null || value === '' ? null : Number(value));
    this.editingChannelId.set(null);
    this.deletePendingChannel.set(null);
    this.manageError.set(null);
  }

  addChannel(): void {
    const groupId = this.manageGroupId();
    const name = (this.newChannel.name || '').trim();
    if (groupId == null || !name) return;
    this.runManage(
      this.communityService.createChannel({
        group_id: groupId,
        name,
        description: (this.newChannel.description || '').trim() || undefined,
        mode: this.newChannel.mode,
        read_only: this.newChannel.read_only
      }, this.lang),
      () => { this.newChannel = { name: '', description: '', mode: 'discussion', read_only: false }; }
    );
  }

  startEditChannel(channel: CommunityChannel): void {
    this.editingChannelId.set(channel.id);
    this.channelEdit = {
      name: channel.name,
      description: channel.description ?? '',
      mode: channel.mode,
      read_only: channel.read_only
    };
  }

  cancelEditChannel(): void {
    this.editingChannelId.set(null);
  }

  saveEditChannel(channel: CommunityChannel): void {
    const name = (this.channelEdit.name || '').trim();
    if (!name) return;
    this.runManage(
      this.communityService.updateChannel(channel.id, {
        name,
        description: (this.channelEdit.description || '').trim(),
        mode: this.channelEdit.mode,
        read_only: this.channelEdit.read_only
      }, this.lang),
      () => this.editingChannelId.set(null)
    );
  }

  toggleArchived(channel: CommunityChannel): void {
    this.runManage(this.communityService.updateChannel(channel.id, { archived: !channel.archived }, this.lang));
  }

  deleteChannel(channel: CommunityChannel, confirmMsg: string): void {
    if (!confirm(confirmMsg)) return;
    this.deletePendingChannel.set(null);
    this.manageError.set(null);
    this.manageBusy.set(true);
    this.communityService.deleteChannel(channel.id).subscribe({
      next: () => this.afterManageChange(channel.id),
      error: err => {
        this.manageBusy.set(false);
        if (err?.status === 422 && this.manageChannels().some(c => c.id !== channel.id)) {
          // Threads inside: the API refuses until told where they go.
          this.deletePendingChannel.set(channel);
          this.deleteMoveTargetId = this.manageChannels().find(c => c.id !== channel.id)?.id ?? null;
        } else {
          this.manageError.set(communityErrorKey(err));
        }
      }
    });
  }

  confirmDeleteWithMove(): void {
    const channel = this.deletePendingChannel();
    const target = this.deleteMoveTargetId;
    if (!channel || target == null) return;
    this.manageBusy.set(true);
    this.communityService.deleteChannel(channel.id, target).subscribe({
      next: () => {
        this.deletePendingChannel.set(null);
        this.afterManageChange(channel.id);
      },
      error: err => {
        this.manageBusy.set(false);
        this.manageError.set(communityErrorKey(err));
      }
    });
  }

  cancelDeleteWithMove(): void {
    this.deletePendingChannel.set(null);
  }

  private runManage(request: Observable<unknown>, onSuccess?: () => void): void {
    this.manageError.set(null);
    this.manageBusy.set(true);
    request.subscribe({
      next: () => {
        onSuccess?.();
        this.afterManageChange();
      },
      error: err => {
        this.manageBusy.set(false);
        this.manageError.set(communityErrorKey(err));
      }
    });
  }

  /** Refresh structure + list after an admin change; leave a deleted current channel. */
  private afterManageChange(deletedChannelId?: number): void {
    this.manageBusy.set(false);
    this.loadOverview();
    if (deletedChannelId != null && this.channelId() === deletedChannelId) {
      this.router.navigateByUrl(this.allLink());
      return;
    }
    this.loadChannel();
    this.load(this.page());
  }

  // --- Muted users (admin) ---------------------------------------------------

  loadHiddenUsers(): void {
    this.communityService.listHiddenUsers().subscribe({
      next: res => this.hiddenUsers.set(res.data),
      error: () => this.hiddenUsers.set([])
    });
  }

  unhideUser(user: CommunityUserBrief): void {
    this.unhideError.set(null);
    this.unhideTarget.set(user);
  }

  confirmUnhide(reason: string): void {
    const user = this.unhideTarget();
    if (!user) return;
    this.unhideBusy.set(true);
    this.unhideError.set(null);
    this.communityService.hideUser(user.id, false, reason).subscribe({
      next: () => {
        this.unhideBusy.set(false);
        this.unhideTarget.set(null);
        this.loadHiddenUsers();
        this.load(this.page());
      },
      error: err => {
        this.unhideBusy.set(false);
        this.unhideError.set(communityErrorKey(err));
      }
    });
  }

  stripHtml(html: string | undefined): string {
    return communityHtmlToText(html);
  }
}
